import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  User, Lock, Loader2, UserPlus, Copy, Check, Globe2, ShieldCheck,
  RefreshCcw, Clock,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const PENDING_KEY = "habthek_pending_reg";

const loadPending = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p?.pending_id || !p?.expires_at) return null;
    if (new Date(p.expires_at).getTime() <= Date.now()) {
      sessionStorage.removeItem(PENDING_KEY);
      return null;
    }
    return p;
  } catch {
    return null;
  }
};

const fmtTime = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const UserRegister = () => {
  const { registerStart, registerVerify, API } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [hotels, setHotels] = useState([]);
  const [habboName, setHabboName] = useState("");
  const [hotel, setHotel] = useState("de");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const [pending, setPending] = useState(() => loadPending());
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/hotels`).then((r) => setHotels(r.data || [])).catch(() => {});
  }, [API]);

  // Ablauf-Countdown (15 Min)
  useEffect(() => {
    if (!pending) return undefined;
    const tick = () => {
      const ms = new Date(pending.expires_at).getTime() - Date.now();
      setRemaining(ms);
      if (ms <= 0) {
        sessionStorage.removeItem(PENDING_KEY);
        setPending(null);
        setFeedback(null);
        toast({ title: "Code abgelaufen", description: "Bitte starte die Registrierung neu.", variant: "destructive" });
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [pending, toast]);

  // Cooldown-Countdown (5s zwischen Versuchen)
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const hotelInfo = useMemo(
    () => hotels.find((h) => h.id === (pending?.hotel || hotel)),
    [hotels, hotel, pending]
  );

  const startRegistration = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await registerStart(habboName.trim(), hotel, password);
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(data));
      setPending(data);
      setFeedback(null);
      setCooldown(0);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({
        title: "Fehler",
        description: typeof d === "string" ? d : "Registrierung fehlgeschlagen",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(pending.verification_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Kopieren fehlgeschlagen", description: pending.verification_code });
    }
  };

  const checkMotto = async () => {
    if (!pending || checking || cooldown > 0) return;
    setChecking(true);
    setFeedback(null);
    try {
      const data = await registerVerify(pending.pending_id);
      if (data.verified) {
        sessionStorage.removeItem(PENDING_KEY);
        toast({ title: "Account erstellt", description: `Willkommen, ${data.habbo_name}! Du bist jetzt eingeloggt.` });
        navigate("/profil");
        return;
      }
      setFeedback({ type: "warn", text: data.message || "Code noch nicht im Habbo-Motto gefunden." });
      setCooldown(5);
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Prüfung fehlgeschlagen – bitte erneut versuchen.";
      if (status === 404 || status === 410) {
        sessionStorage.removeItem(PENDING_KEY);
        setPending(null);
        toast({ title: "Registrierung abgelaufen", description: msg, variant: "destructive" });
      } else if (status === 429) {
        setFeedback({ type: "warn", text: msg });
        setCooldown(5);
      } else {
        setFeedback({ type: "error", text: msg });
        setCooldown(5);
      }
    } finally {
      setChecking(false);
    }
  };

  const restart = () => {
    sessionStorage.removeItem(PENDING_KEY);
    setPending(null);
    setFeedback(null);
    setCooldown(0);
  };

  // ---------- Schritt 2: Code in Mission setzen ----------
  if (pending) {
    return (
      <section className="mx-auto max-w-md px-4 py-20">
        <div className="rounded-2xl border border-white/10 bg-[#0f141b] p-8" data-testid="register-step2">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d7bff]">
            <ShieldCheck className="h-4 w-4" /> Habbo-Verifizierung
          </div>
          <h1 className="mt-1 text-2xl font-bold text-white">Code in deine Mission setzen</h1>
          <p className="mt-2 text-sm text-zinc-400">
            Hallo <span className="font-semibold text-zinc-200">{pending.habbo_name}</span>! Setze diesen
            Code in dein Habbo-Motto (Mission) auf{" "}
            <span className="font-semibold text-zinc-200">{hotelInfo?.label || pending.hotel_label || pending.hotel}</span>.
          </p>

          <div className="mt-5 flex items-center gap-2 rounded-xl border border-[#9d7bff]/30 bg-[#161129] px-4 py-3">
            <code className="flex-1 select-all font-mono text-lg font-bold tracking-wider text-[#c9b3ff]" data-testid="register-verification-code">
              {pending.verification_code}
            </code>
            <Button type="button" onClick={copyCode} data-testid="register-copy-code" className="btn-ghost rounded-lg px-3 py-2 text-xs">
              {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
              <span className="ml-1">{copied ? "Kopiert" : "Kopieren"}</span>
            </Button>
          </div>

          <ol className="mt-5 space-y-2 text-sm text-zinc-400">
            <li><span className="font-semibold text-zinc-300">1.</span> Logge dich auf <span className="text-zinc-200">{pending.hotel_domain || "habbo.de"}</span> ein.</li>
            <li><span className="font-semibold text-zinc-300">2.</span> Öffne dein Profil → Einstellungen → Mission (Motto).</li>
            <li><span className="font-semibold text-zinc-300">3.</span> Füge den Code ein und speichere.</li>
            <li><span className="font-semibold text-zinc-300">4.</span> Klicke unten auf „Ich habe den Code gesetzt".</li>
          </ol>

          <div className="mt-4 flex items-center gap-2 text-xs text-zinc-500">
            <Clock className="h-3.5 w-3.5" />
            Code läuft ab in <span className="font-mono font-semibold text-zinc-300" data-testid="register-expiry-timer">{fmtTime(remaining)}</span>
          </div>

          {feedback && (
            <div
              data-testid="register-verify-feedback"
              className={`mt-4 rounded-lg border px-3 py-2 text-sm ${
                feedback.type === "error"
                  ? "border-rose-500/30 bg-rose-500/10 text-rose-300"
                  : "border-amber-500/30 bg-amber-500/10 text-amber-200"
              }`}
            >
              {feedback.text}
            </div>
          )}

          <Button
            type="button"
            onClick={checkMotto}
            disabled={checking || cooldown > 0}
            data-testid="register-verify-btn"
            className="btn-primary mt-5 w-full rounded-lg py-2 text-sm font-semibold"
          >
            {checking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {cooldown > 0 ? `Erneut prüfen in ${cooldown}s…` : "Ich habe den Code gesetzt"}
          </Button>
          <p className="mt-2 text-center text-[11px] text-zinc-500">
            Habbo cached dein Profil ~15–30 Sekunden – ggf. kurz warten.
          </p>

          <button
            type="button"
            onClick={restart}
            data-testid="register-restart"
            className="mt-4 flex w-full items-center justify-center gap-1 text-xs text-zinc-500 hover:text-zinc-300"
          >
            <RefreshCcw className="h-3 w-3" /> Registrierung neu starten
          </button>
        </div>
      </section>
    );
  }

  // ---------- Schritt 1: Formular ----------
  return (
    <section className="mx-auto max-w-md px-4 py-20">
      <div className="rounded-2xl border border-white/10 bg-[#0f141b] p-8" data-testid="register-step1">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d7bff]">
          <UserPlus className="h-4 w-4" /> Habthek
        </div>
        <h1 className="mt-1 text-2xl font-bold text-white">Registrieren</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Keine E-Mail nötig – du verifizierst dich direkt über deine Habbo-Mission (Motto).
        </p>
        <form onSubmit={startRegistration} className="mt-6 space-y-3">
          <div className="relative">
            <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="register-habbo-name" value={habboName} onChange={(e) => setHabboName(e.target.value)} placeholder="Dein Habbo-Name (exakt)" className="pl-9 bg-[#10141b] border-white/10 text-zinc-200" />
          </div>
          <div className="relative">
            <Globe2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <select
              data-testid="register-hotel"
              value={hotel}
              onChange={(e) => setHotel(e.target.value)}
              className="h-9 w-full appearance-none rounded-md border border-white/10 bg-[#10141b] pl-9 pr-8 text-sm text-zinc-200 outline-none focus:border-[#9d7bff]/50"
            >
              {(hotels.length ? hotels : [{ id: "de", flag: "🇩🇪", label: "Habbo Deutschland" }]).map((h) => (
                <option key={h.id} value={h.id}>{h.flag} {h.label}</option>
              ))}
            </select>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Passwort (min. 6 Zeichen)" className="pl-9 bg-[#10141b] border-white/10 text-zinc-200" />
          </div>
          <Button type="submit" data-testid="register-submit" disabled={busy || !habboName.trim() || password.length < 6} className="btn-primary w-full rounded-lg py-2 text-sm font-semibold">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Code generieren
          </Button>
        </form>
        <div className="mt-4 text-center text-sm text-zinc-500">
          Schon einen Account? <Link to="/login" className="text-[#b39bff] hover:text-white">Einloggen</Link>
        </div>
      </div>
    </section>
  );
};

export default UserRegister;
